import { type CSSProperties, useContext, useEffect, useMemo, useState } from "react";
import { Button, Empty, Input, message } from "antd";
import {
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  PlusOutlined,
  RightOutlined,
  SearchOutlined,
} from "@ant-design/icons";

import RebetLoader from "../../../CommonComponents/RebetLoader/RebetLoader";
import AppContext from "../../../../context/AppContext";
import { SIDER_TABS } from "../../../../utils/Constants";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { formatString } from "../../../../utils/constants/stringUtils";
import {
  useAdminCollections,
  type AdminCollection,
} from "../../../../queries/casino-configuration/collections/useAdminCollections";
import {
  useDeleteAdminCollection,
  useToggleAdminCollection,
} from "../../../../queries/casino-configuration/collections/useSaveAdminCollection";
import fallbackCollectionIcon from "../../../../assets/icons/ic_casino.svg";
import CollectionDetailModal from "./pages/CollectionDetailModal";
import NewCollectionModal from "./pages/NewCollectionModal";
import CurationEntityName from "../CurationEntityName";
import { useCurationDisplayName } from "../../../../hooks/useCurationDisplayName";
import DeleteCollectionConfirmModal, {
  type DeleteCollectionTarget,
} from "./pages/DeleteCollectionConfirmModal";
import {
  getCollectionKindLabel,
  isSpecialCollection,
} from "./collectionHelpers";
import { useCanUploadCasinoFmV3Assets } from "../../../../utils/casinoFmPermissions";
import { usePageTitle } from "../../../CommonComponents/Layout/PageTitleContext";
import { getApiErrorMessage } from "../../../../utils/ErrorUtils";

import "./stylesCollectionsConfiguration.css";

const SEARCH_DEBOUNCE_MS = 300;
const COLLECTION_ROW_INDENT_PX = 28;
const EMPTY_ANCESTORS: ReadonlySet<string> = new Set();

type RowHandlers = {
  /** Flag- and locale-aware display name (canonical when the flag is off). */
  getName: (collection: AdminCollection) => string;
  onEdit: (code: string) => void;
  onDelete: (collection: AdminCollection) => void;
  onToggleExpand: (code: string) => void;
  onToggleActive: (collection: AdminCollection) => Promise<void>;
  expandedKeys: Set<string>;
  togglingCodes: Set<string>;
};

function CollectionRow({
  collection,
  depth,
  ancestorCodes,
  ...handlers
}: {
  collection: AdminCollection;
  depth: number;
  ancestorCodes: ReadonlySet<string>;
} & RowHandlers) {
  const hasSubs = (collection.child_count ?? 0) > 0;
  const isExpanded = handlers.expandedKeys.has(collection.code);
  const isToggling = handlers.togglingCodes.has(collection.code);
  const isChild = depth > 0;
  const isSpecial = isSpecialCollection(collection.code);
  const displayName = handlers.getName(collection);

  return (
    <>
      <div
        className={`collections-row ${
          isChild ? "collections-row--child" : ""
        }`}
        style={
          depth > 1
            ? ({
                "--collections-row-extra-indent": `${
                  (depth - 1) * COLLECTION_ROW_INDENT_PX
                }px`,
              } as CSSProperties)
            : undefined
        }
        data-testid={`collection-row-${collection.code}`}
      >
        <div className="collections-row-left">
          {hasSubs ? (
            <button
              type="button"
              className="collections-expand-btn"
              onClick={() => handlers.onToggleExpand(collection.code)}
              aria-label={`${
                isExpanded
                  ? AppStrings.CASINO_CONFIG_COLLECTIONS_ARIA_COLLAPSE
                  : AppStrings.CASINO_CONFIG_COLLECTIONS_ARIA_EXPAND
              } ${displayName}`}
              aria-expanded={isExpanded}
            >
              {isExpanded ? <DownOutlined /> : <RightOutlined />}
            </button>
          ) : (
            <span className="collections-expand-placeholder" />
          )}
          <img
            className="collections-icon"
            src={collection.icon_url || fallbackCollectionIcon}
            alt=""
          />
          <CurationEntityName
            className="collections-name"
            entity={collection}
            displayName={displayName}
          />
        </div>

        <div className="collections-row-right">
          <span
            className={`collections-type-badge ${
              collection.kind === "providers"
                ? "collections-type-badge--providers"
                : "collections-type-badge--games"
            }`}
          >
            {getCollectionKindLabel(collection.kind)}
          </span>
          {hasSubs ? (
            <span className="collections-sub-count">
              {collection.child_count}{" "}
              {AppStrings.CASINO_CONFIG_COLLECTIONS_SUB_SUFFIX}
            </span>
          ) : null}
          <span className="collections-item-count">
            {collection.member_count ?? 0}{" "}
            {collection.kind === "providers"
              ? AppStrings.CASINO_CONFIG_COLLECTIONS_PROVIDERS_SUFFIX
              : AppStrings.CASINO_CONFIG_COLLECTIONS_GAMES_SUFFIX}
          </span>
          <span
            className={`collections-active-label ${
              collection.is_active
                ? "collections-active-label--on"
                : "collections-active-label--off"
            }`}
          >
            {collection.is_active
              ? AppStrings.CASINO_CONFIG_COLLECTIONS_TOGGLE_ON
              : AppStrings.CASINO_CONFIG_COLLECTIONS_TOGGLE_OFF}
          </span>
          {/*
            * The active toggle stays enabled for special collections: unlike
            * their (backend-computed) members, `is_active` controls whether the
            * placement is shown to players. Name/icon edit is also enabled
            * (CASENG-1066); only member add/remove and delete stay blocked.
            */}
          <label className="collections-toggle-switch">
            <input
              type="checkbox"
              checked={collection.is_active}
              disabled={isToggling}
              onChange={() => void handlers.onToggleActive(collection)}
              aria-label={`${
                collection.is_active
                  ? AppStrings.CASINO_CONFIG_COLLECTIONS_ARIA_DEACTIVATE
                  : AppStrings.CASINO_CONFIG_COLLECTIONS_ARIA_ACTIVATE
              } ${displayName}`}
            />
            <span className="collections-toggle-slider" />
          </label>
          <button
            type="button"
            className="collections-edit-btn"
            aria-label={formatString(
              AppStrings.CASINO_CONFIG_COLLECTIONS_EDIT_ROW_ARIA,
              { name: displayName },
            )}
            onClick={() => handlers.onEdit(collection.code)}
            data-testid={`collection-edit-${collection.code}`}
          >
            <EditOutlined />
          </button>
          {/*
            * Inline delete affordance — same `is_seed` guard as the detail
            * modal's own footer button so seed collections (e.g. `all_games`)
            * never show a clickable trash icon. Surfacing delete on the row
            * saves admins from having to open the detail modal just to
            * remove a stale test collection.
            *
            * Special (placement-only) collections are also excluded: deleting one
            * that the Features/Views config still references would silently orphan
            * that placement, so they can't be removed from the catalog.
            */}
          {!collection.is_seed && !isSpecial ? (
            <button
              type="button"
              className="collections-delete-btn"
              aria-label={formatString(
                AppStrings.CASINO_CONFIG_COLLECTIONS_DELETE_ROW_ARIA,
                { name: displayName },
              )}
              onClick={() => handlers.onDelete(collection)}
              data-testid={`collection-delete-${collection.code}`}
            >
              <DeleteOutlined />
            </button>
          ) : null}
        </div>
      </div>

      {isExpanded && hasSubs ? (
        <ChildCollectionsList
          parentCode={collection.code}
          depth={depth + 1}
          ancestorCodes={ancestorCodes}
          {...handlers}
        />
      ) : null}
    </>
  );
}

function ChildCollectionsList({
  parentCode,
  depth,
  ancestorCodes,
  ...handlers
}: {
  parentCode: string;
  depth: number;
  ancestorCodes: ReadonlySet<string>;
} & RowHandlers) {
  const { data, isLoading } = useAdminCollections({ parentCode });
  // Guard against cycles — if the API ever returns a child whose code is one of
  // its own ancestors (or the parent itself), skip it to avoid infinite recursion.
  const nextAncestors = useMemo(() => {
    const next = new Set(ancestorCodes);
    next.add(parentCode);
    return next;
  }, [ancestorCodes, parentCode]);
  const children = useMemo(
    () => (data?.items ?? []).filter((c) => !nextAncestors.has(c.code)),
    [data, nextAncestors],
  );

  if (isLoading) {
    return (
      <div className="collections-child-loader">
        <RebetLoader height="32px" width="32px" />
      </div>
    );
  }

  return (
    <>
      {children.map((child) => (
        <CollectionRow
          key={child.code}
          collection={child}
          depth={depth}
          ancestorCodes={nextAncestors}
          {...handlers}
        />
      ))}
    </>
  );
}

function CollectionsConfigurationPage() {
  usePageTitle(AppStrings.CASINO_CONFIG_COLLECTIONS_TITLE);
  const { setActiveSidebarTab } = useContext(AppContext);
  const canUploadCasinoFmV3Assets = useCanUploadCasinoFmV3Assets();
  const { getName: getCollectionDisplayName } = useCurationDisplayName();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(
    () => new Set(),
  );
  const [detailCode, setDetailCode] = useState<string | null>(null);
  const [newCollectionOpen, setNewCollectionOpen] = useState(false);
  /**
   * Delete-modal state is intentionally split into two fields:
   *   - `deleteTarget` holds the row being acted on (its display data).
   *   - `deleteOpen` drives antd's open/close animation lifecycle.
   * On success/cancel we flip `deleteOpen` to false to trigger the
   * slide-out, then clear `deleteTarget` from `afterClose` once antd has
   * finished animating. Clearing both in the same render makes the modal
   * vanish abruptly without playing the transition.
   */
  const [deleteTarget, setDeleteTarget] =
    useState<DeleteCollectionTarget | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { mutateAsync: toggleCollection } = useToggleAdminCollection();
  const { mutateAsync: deleteCollection, isPending: deleting } =
    useDeleteAdminCollection();
  const [togglingCodes, setTogglingCodes] = useState<Set<string>>(new Set());

  useEffect(() => {
    setActiveSidebarTab(SIDER_TABS.CASINO, "CONFIGURATION");
  }, [setActiveSidebarTab]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timeout);
  }, [searchQuery]);

  // Searching reframes which rows are in view, so the previous expansion state
  // no longer makes sense — collapse everything when the active query changes.
  // Bail when nothing is expanded so we don't trigger a redundant re-render.
  useEffect(() => {
    setExpandedKeys((prev) => (prev.size === 0 ? prev : new Set()));
  }, [debouncedSearch]);

  const {
    data: collections,
    isLoading,
    isError,
    error,
  } = useAdminCollections(
    debouncedSearch ? { search: debouncedSearch } : undefined,
  );

  const collectionsList = useMemo(
    () => collections?.items ?? [],
    [collections],
  );

  const stats = useMemo(
    () => ({
      total: collections?.total ?? collectionsList.length,
      active: collections?.active ?? collectionsList.filter((c) => c.is_active).length,
    }),
    [collections, collectionsList],
  );

  const toggleExpand = (code: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    });
  };

  const handleToggleActive = async (collection: AdminCollection) => {
    setTogglingCodes((prev) => new Set(prev).add(collection.code));
    try {
      await toggleCollection({
        code: collection.code,
        is_active: !collection.is_active,
      });
    } catch (err) {
      message.error(
        getApiErrorMessage(
          err,
          formatString(AppStrings.CASINO_CONFIG_COLLECTIONS_TOGGLE_FAILED, {
            name: getCollectionDisplayName(collection),
          }),
        ),
      );
    } finally {
      setTogglingCodes((prev) => {
        const next = new Set(prev);
        next.delete(collection.code);
        return next;
      });
    }
  };

  const handleOpenDelete = (collection: AdminCollection) => {
    setDeleteTarget({
      code: collection.code,
      name: collection.name,
      name_translations: collection.name_translations,
      kind: collection.kind,
      member_count: collection.member_count,
    });
    setDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteCollection(deleteTarget.code);
      message.success(AppStrings.CASINO_CONFIG_COLLECTIONS_DELETE_SUCCESS);
      // Kick off the antd close transition; the `afterClose` callback on
      // the modal will null out `deleteTarget` once the animation lands.
      setDeleteOpen(false);
    } catch (err: unknown) {
      // BE returns the specific blocker reason (subcollections still attached,
      // referenced by a view placement, seed collection guard, etc.) — surface
      // it verbatim so the admin can act on it without checking the network tab.
      message.error(
        getApiErrorMessage(
          err,
          (err as Error)?.message ||
            AppStrings.CASINO_CONFIG_COLLECTIONS_DELETE_FAILED,
        ),
      );
    }
  };

  const rowHandlers: RowHandlers = {
    getName: getCollectionDisplayName,
    onEdit: setDetailCode,
    onDelete: handleOpenDelete,
    onToggleExpand: toggleExpand,
    onToggleActive: handleToggleActive,
    expandedKeys,
    togglingCodes,
  };

  return (
    <>
      <div className="collections-shell">
        <div className="collections-config">
          <div className="collections-header">
            <div>
              <p className="collections-subtitle">
                {AppStrings.CASINO_CONFIG_COLLECTIONS_SUBTITLE}
              </p>
            </div>
            <Button
              className="collections-new-btn"
              icon={<PlusOutlined />}
              type="primary"
              onClick={() => setNewCollectionOpen(true)}
            >
              {AppStrings.CASINO_CONFIG_COLLECTIONS_NEW}
            </Button>
          </div>

          <div className="collections-controls">
            <Input
              className="collections-search"
              prefix={<SearchOutlined />}
              placeholder={AppStrings.CASINO_CONFIG_COLLECTIONS_SEARCH_PLACEHOLDER}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              allowClear
            />
            <div className="collections-stats">
              <span>
                <b>{stats.total}</b>{" "}
                {AppStrings.CASINO_CONFIG_COLLECTIONS_STAT_COLLECTIONS}
              </span>
              <span>
                <b>{stats.active}</b>{" "}
                {AppStrings.CASINO_CONFIG_COLLECTIONS_STAT_ACTIVE}
              </span>
            </div>
          </div>

          <div className="collections-list">
            {isLoading ? (
              <div className="collections-loader">
                <RebetLoader height="88px" width="88px" />
              </div>
            ) : isError ? (
              <div className="collections-empty">
                <Empty
                  description={getApiErrorMessage(
                    error,
                    (error as Error)?.message ||
                      AppStrings.CASINO_CONFIG_COLLECTIONS_LOAD_ERROR,
                  )}
                />
              </div>
            ) : collectionsList.length === 0 ? (
              <div className="collections-empty">
                <Empty
                  description={AppStrings.CASINO_CONFIG_COLLECTIONS_EMPTY}
                />
              </div>
            ) : (
              collectionsList.map((collection) => (
                <CollectionRow
                  key={collection.code}
                  collection={collection}
                  depth={0}
                  ancestorCodes={EMPTY_ANCESTORS}
                  {...rowHandlers}
                />
              ))
            )}
          </div>
        </div>
      </div>

      <CollectionDetailModal
        open={Boolean(detailCode)}
        collectionCode={detailCode}
        onClose={() => setDetailCode(null)}
        onNavigate={(code) => setDetailCode(code)}
        canUploadImages={canUploadCasinoFmV3Assets}
      />

      <NewCollectionModal
        open={newCollectionOpen}
        onClose={() => setNewCollectionOpen(false)}
        canUploadImages={canUploadCasinoFmV3Assets}
      />

      <DeleteCollectionConfirmModal
        open={deleteOpen}
        collection={deleteTarget}
        deleting={deleting}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={handleConfirmDelete}
        afterClose={() => setDeleteTarget(null)}
      />
    </>
  );
}

export default CollectionsConfigurationPage;
